// Адаптер данных кошельков корп. клиента.
// Единственное место, которое знает формат wallet_statistics и wallet-dynamics бэка.
// Если бэк поменяет формат — правим только этот файл, экраны работают с результатом parse*.
//
// Правила бэка, которые важно не нарушать на экранах:
// - остатки складываются: total.balance = buffer.balance + Σ managers[].balance;
// - обороты (received/spent) строк НЕ складываются: раздача из буфера менеджеру видна в строках,
//   но исключена из total, поэтому total.received/spent берём только из total;
// - spent — назначение кандидату (а для буфера ещё и раздача менеджерам), не «пройдено»;
// - в динамике только обороты: остаток на дату из них не строится.

export const DATA_QUALITY_FLAGS = [
  'excluded_movements',
  'negative_quantity',
  'ambiguous_self_correction',
  'unmapped_movements',
  'unmapped_wallets',
  'nonzero_auxiliary_wallets',
];

// Подписи причин «данные требуют сверки» для админа.
export const DATA_QUALITY_LABELS = {
  excluded_movements: 'Операции, исключённые из расчёта',
  negative_quantity: 'Операции с отрицательным количеством',
  ambiguous_self_correction: 'Неоднозначные исправления',
  unmapped_movements: 'Операции без определённой услуги',
  unmapped_wallets: 'Кошельки без владельца',
  nonzero_auxiliary_wallets: 'Остаток на техническом менеджере',
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
// Только объекты: пустые и битые элементы массивов бэка пропускаем, чтобы экран не падал.
const asList = (list) => (Array.isArray(list) ? list.filter((x) => x && typeof x === 'object') : []);

const parseAmounts = (raw = {}) => ({
  balance: num(raw && raw.balance),
  received: num(raw && raw.received),
  spent: num(raw && raw.spent),
});

const parseBuffer = (raw = {}) => ({
  corpClientId: raw && raw.corp_client_id != null ? raw.corp_client_id : null,
  ...parseAmounts(raw),
});

const parseManagers = (list) => asList(list).map((m) => ({
  id: m.manager_id,
  name: m.name || '—',
  ...parseAmounts(m),
}));

export const hasAmounts = (a) => !!(a.balance || a.received || a.spent);

// Менеджеры корпа из сводки: остаток и обороты по всем тестам плюс раскладка по тестам.
function collectManagers(items) {
  const byId = new Map();
  items.forEach((item) => {
    item.managers.forEach((m) => {
      if (!byId.has(m.id)) {
        byId.set(m.id, {
          id: m.id, name: m.name, balance: 0, received: 0, spent: 0, tests: [],
        });
      }
      const acc = byId.get(m.id);
      acc.balance += m.balance;
      acc.received += m.received;
      acc.spent += m.spent;
      acc.tests.push({
        id: item.id, code: item.code, name: item.name, balance: m.balance, received: m.received, spent: m.spent,
      });
    });
  });
  return [...byId.values()];
}

export function parseDataQuality(raw) {
  const flags = DATA_QUALITY_FLAGS.reduce((acc, key) => {
    acc[key] = num(raw && raw[key]);
    return acc;
  }, {});
  // Счётчики между собой не суммируются (одна запись может попасть в несколько) — нужен только признак.
  return { flags, hasIssues: DATA_QUALITY_FLAGS.some((key) => flags[key] > 0) };
}

// Блок wallet_statistics в ответе get-me (корп смотрит себя): на верхнем уровне или во вложенном объекте корпа.
export function findWalletStatistics(me) {
  if (!me || typeof me !== 'object') return null;
  if (me.wallet_statistics) return me.wallet_statistics;
  const nested = Object.values(me).find((v) => v && typeof v === 'object' && v.wallet_statistics);
  return nested ? nested.wallet_statistics : null;
}

/**
 * wallet_statistics → модель для экранов.
 * Возвращает null, если сводки нет: например, у менеджера в чужой карточке корпа — нулями не подменяем.
 */
export function parseWalletStatistics(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.balance == null && !Array.isArray(raw.nomenclatures)) return null;

  const items = asList(raw.nomenclatures).map((n) => ({
    id: n.nomenclature_id,
    code: n.code || '',
    name: n.name || n.code || '—',
    instrumentSetId: n.instrument_set_id != null ? n.instrument_set_id : null,
    total: parseAmounts(n.total),
    buffer: parseBuffer(n.buffer),
    managers: parseManagers(n.managers),
  }));

  const um = raw.unmapped_movements || {};
  const unmapped = {
    total: parseAmounts(um.total),
    buffer: parseBuffer(um.buffer),
    managers: parseManagers(um.managers),
    movements: asList(um.movements).map((m) => ({
      code: m.code || '',
      instrumentSetId: m.instrument_set_id != null ? m.instrument_set_id : null,
      total: parseAmounts(m.total),
      buffer: parseBuffer(m.buffer),
      managers: parseManagers(m.managers),
    })),
  };
  unmapped.hasData = hasAmounts(unmapped.total) || unmapped.movements.length > 0;

  const dataQuality = parseDataQuality(raw.data_quality);

  return {
    period: {
      from: (raw.period && raw.period.from_date) || null,
      to: (raw.period && raw.period.to_date) || null,
    },
    // Плашки — сумма по тестам. Верхние balance/received/spent сводки не используем:
    // там смешаны все услуги и «неопределённые» движения (отчёт бэка, «Чего делать нельзя»).
    totals: items.reduce((acc, i) => ({
      balance: acc.balance + i.total.balance,
      received: acc.received + i.total.received,
      spent: acc.spent + i.total.spent,
    }), { balance: 0, received: 0, spent: 0 }),
    // Нераспределённый остаток корпа по всем тестам.
    bufferBalance: items.reduce((sum, i) => sum + i.buffer.balance, 0),
    // Остаток у всех менеджеров по всем тестам.
    managersBalance: items.reduce((sum, i) => sum + i.managers.reduce((s, m) => s + m.balance, 0), 0),
    managers: collectManagers(items),
    items,
    unmapped,
    dataQuality,
    // Пометка «Данные требуют сверки» для админа.
    needsReconciliation: dataQuality.hasIssues || unmapped.hasData,
  };
}

const parseFlow = (raw = {}) => ({ received: num(raw && raw.received), spent: num(raw && raw.spent) });

const parsePoints = (list) => asList(list).map((p) => ({
  from: p.from_date,
  to: p.to_date,
  total: parseFlow(p.total),
  buffer: {
    corpClientId: p.buffer && p.buffer.corp_client_id != null ? p.buffer.corp_client_id : null,
    ...parseFlow(p.buffer),
  },
  managers: asList(p.managers).map((m) => ({ id: m.manager_id, name: m.name || '—', ...parseFlow(m) })),
  dataQuality: parseDataQuality(p.data_quality),
}));

/** Ответ GET /users/corp_clients/{id}/wallet-dynamics/ → модель для графиков. */
export function parseWalletDynamics(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const um = raw.unmapped_movements || {};
  return {
    period: {
      from: (raw.period && raw.period.from_date) || null,
      to: (raw.period && raw.period.to_date) || null,
    },
    groupBy: raw.group_by || 'day',
    nomenclatureId: raw.nomenclature_id != null ? raw.nomenclature_id : null,
    totals: parseFlow(raw),
    items: asList(raw.nomenclatures).map((n) => ({
      id: n.nomenclature_id,
      code: n.code || '',
      name: n.name || n.code || '—',
      instrumentSetId: n.instrument_set_id != null ? n.instrument_set_id : null,
      ...parseFlow(n),
      points: parsePoints(n.points),
    })),
    unmapped: { ...parseFlow(um), points: parsePoints(um.points) },
    dataQuality: parseDataQuality(raw.data_quality),
  };
}

/**
 * Владелец старой записи (кандидат, сессия и т.п.).
 * Технический менеджер корпа (is_auxiliary) показывается как сам корп, без перехода в карточку менеджера.
 */
export function resolveOwner(manager) {
  if (!manager || typeof manager !== 'object') return null;
  const owner = manager.display_owner;
  if (manager.is_auxiliary && owner && owner.type === 'corpclient') {
    return { type: 'corpclient', id: owner.id, isAuxiliary: true };
  }
  return { type: 'manager', id: owner && owner.id != null ? owner.id : manager.id, isAuxiliary: false };
}
